/**
 * CostOfCreditCard — renders the `get_cost_of_credit` tool result (T-40).
 *
 * Chat adaptation of the dashboard CostBreakdownCard (NOT a reuse). The hero is
 * the all-in cost of credit (interest + mandatory fees, principal excluded),
 * rendered in the ORANGE accent (founder decision 2026-07-06). The figure is the
 * engine's `total_cost` verbatim, so it matches the dashboard to the cent — the
 * fee ledger below reconciles Interest + Arrangement fee to that same total.
 *
 * ENGINE CONTRACT parity: compute_cost_snapshot's total_cost EXCLUDES the
 * principal (Decision #38), the contingent commitment fee (F2), and any separate
 * upfront fee (those feed the effective-rate IRR, not the total). Both are
 * disclosed below, never silently folded in or dropped.
 *
 * Degradation:
 *   no term sheet / no extraction  → render nothing, prose carries.
 *   missing core input (rate/term) → the missing-field disclosure, NOT a
 *                                    confident (fallback-computed) total.
 */
import type { ReactNode } from 'react';

import { Info, PieChart } from 'react-feather';

import { FinancialNumber } from '../../../components/ui';
import type { ToolResultProps } from '../lib/component-registry';

interface CostOfCreditResult {
  available?: boolean;
  reason?: string;
  monthly_payment?: number | null;
  total_interest?: number | null;
  total_fees?: number | null;
  total_cost?: number | null;
  apr_equiv?: number | null;
  shown_fee_count?: number | null;
  missing_fee_count?: number | null;
  missing_core_fields?: string[] | null;
  has_arrangement_fee?: boolean;
  has_commitment_fee?: boolean;
  has_upfront_fee?: boolean;
  arrangement_fee_pct?: number | null;
  commitment_fee_pct?: number | null;
  upfront_fee_amount?: number | null;
  prepayment_penalty_pct?: number | null;
  is_revolving?: boolean;
  is_floating_rate?: boolean;
}

const CORE_FIELD_LABELS: Record<string, string> = {
  principal: 'loan amount',
  rate: 'interest rate',
  term: 'loan term',
};

const EXPECTED_FEE_CATEGORIES = 4;

function isNum(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function fmtPct(v: number): string {
  return `${v.toFixed(2).replace('.', ',')} %`;
}

export function CostOfCreditCard({ result }: ToolResultProps) {
  const r = (result ?? {}) as CostOfCreditResult;

  // No core data at all → prose carries, no card.
  if (r.reason === 'no_term_sheet' || r.reason === 'no_extraction') {
    return null;
  }

  const missingCore = r.missing_core_fields ?? [];

  return (
    <div className="animate-card-enter mt-2 border border-border bg-surface-card">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <span className="shrink-0 text-orange">
            <PieChart size={16} />
          </span>
          <p className="text-caption font-semibold uppercase tracking-wide text-txt-secondary">
            Cost of credit
          </p>
        </div>
        <span className="shrink-0 bg-surface-subtle px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-txt-secondary">
          {r.is_revolving ? 'Revolving' : r.is_floating_rate ? 'Floating' : 'Fixed'}
        </span>
      </div>

      {r.available === false ? (
        <MissingCoreBody fields={missingCore} />
      ) : (
        <FullBody r={r} />
      )}
    </div>
  );
}

function MissingCoreBody({ fields }: { fields: string[] }) {
  const human = fields.map((f) => CORE_FIELD_LABELS[f] ?? f);
  return (
    <div className="px-4 py-3">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-txt-tertiary">
        Total cost of credit
      </span>
      <p className="mt-1.5 text-body font-semibold text-txt-primary">Not computable yet</p>
      <div className="mt-2 flex items-start gap-2 bg-surface-subtle px-3 py-2.5">
        <span className="mt-px shrink-0 text-txt-tertiary">
          <Info size={14} />
        </span>
        <p className="text-[10.5px] leading-relaxed text-txt-tertiary">
          The total cost needs the {human.length > 0 ? human.join(' and ') : 'core loan terms'} to
          compute. {human.length > 0 ? `Missing: ${human.join(', ')}.` : ''} Upload or add the
          missing terms and this will resolve.
        </p>
      </div>
    </div>
  );
}

function FullBody({ r }: { r: CostOfCreditResult }) {
  const totalCost = isNum(r.total_cost) ? r.total_cost : null;
  const arrangementEur = isNum(r.total_fees) ? r.total_fees : null;
  const upfrontExtra = isNum(r.upfront_fee_amount) ? r.upfront_fee_amount : 0;
  const shown = isNum(r.shown_fee_count) ? r.shown_fee_count : EXPECTED_FEE_CATEGORIES;
  const missingFees = isNum(r.missing_fee_count) ? r.missing_fee_count : 0;

  return (
    <>
      {/* Hero — orange total (founder decision 2026-07-06) */}
      <div className="px-4 py-3">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-txt-tertiary">
          Total cost of credit
        </span>
        <div className="mt-1.5">
          {totalCost !== null ? (
            <FinancialNumber
              value={totalCost}
              format="currency"
              size="lg"
              className="!text-orange"
            />
          ) : (
            <span className="font-mono text-mono-lg text-txt-tertiary">—</span>
          )}
        </div>
        <p className="mt-2 text-caption text-txt-tertiary">
          Interest plus fees, <span className="text-txt-secondary">principal excluded</span>.
        </p>
      </div>

      {/* Stat pair — effective rate (IRR) + monthly payment */}
      <div className="grid grid-cols-2 gap-px border-y border-border bg-border">
        <Stat label="Effective rate" hint="IRR estimate · not a statutory APR/TAEG">
          {isNum(r.apr_equiv) && r.apr_equiv > 0 ? fmtPct(r.apr_equiv) : '—'}
        </Stat>
        <Stat label="Monthly payment" hint="first scheduled instalment">
          {isNum(r.monthly_payment) && r.monthly_payment > 0 ? (
            <FinancialNumber value={r.monthly_payment} format="currency" size="sm" />
          ) : (
            '—'
          )}
        </Stat>
      </div>

      {/* Fee ledger — reconciles Interest + Arrangement to the hero total */}
      <div className="px-4 pb-3 pt-1">
        <LedgerRow label="Interest" hint={r.is_floating_rate ? 'floating base + margin' : undefined}>
          {isNum(r.total_interest) ? (
            <FinancialNumber value={r.total_interest} format="currency" size="sm" />
          ) : (
            '—'
          )}
        </LedgerRow>
        {r.has_arrangement_fee && arrangementEur !== null && (
          <LedgerRow
            label="Arrangement fee"
            hint={
              isNum(r.arrangement_fee_pct) && r.arrangement_fee_pct > 0
                ? `once, ${fmtPct(r.arrangement_fee_pct)}`
                : 'once'
            }
          >
            <FinancialNumber value={arrangementEur} format="currency" size="sm" />
          </LedgerRow>
        )}
        <div className="mt-1 flex items-baseline justify-between gap-3 border-t-2 border-border pt-2.5">
          <span className="text-body-sm font-semibold text-txt-primary">Total cost of credit</span>
          <span className="font-mono text-body-sm font-bold tabular-nums text-txt-primary">
            {totalCost !== null ? (
              <FinancialNumber value={totalCost} format="currency" size="sm" />
            ) : (
              '—'
            )}
          </span>
        </div>
      </div>

      {/* Disclosures — commitment fee, separate upfront fee, fee coverage */}
      <div className="flex flex-col gap-2 border-t border-border px-4 py-3">
        {r.has_commitment_fee && isNum(r.commitment_fee_pct) && r.commitment_fee_pct > 0 && (
          <p className="text-[11px] leading-relaxed text-txt-secondary">
            A <span className="font-semibold text-txt-primary">{fmtPct(r.commitment_fee_pct)}</span>{' '}
            commitment fee applies to any undrawn balance during the availability period. It is
            excluded from the total above because it depends on how much stays undrawn.
          </p>
        )}
        {r.has_upfront_fee && upfrontExtra > 0 && (
          <p className="text-[11px] leading-relaxed text-txt-secondary">
            A separate upfront fee of{' '}
            <span className="font-mono text-txt-primary">
              <FinancialNumber value={upfrontExtra} format="currency" size="sm" />
            </span>{' '}
            was found; it is paid at signing and is not part of the cost-of-credit total.
          </p>
        )}
        <p className="text-[10.5px] leading-relaxed text-txt-tertiary">
          {shown} of {EXPECTED_FEE_CATEGORIES} fee categories captured
          {missingFees > 0 ? `; ${missingFees} not found in this term sheet` : ''}. The effective
          rate is an IRR estimate, not a statutory APR/TAEG.
        </p>
      </div>

      {/* Footer */}
      <div className="border-t border-border px-4 py-2">
        <span className="text-overline uppercase tracking-wide text-txt-tertiary">
          Source: uploaded term sheet
        </span>
      </div>
    </>
  );
}

function Stat({
  label,
  hint,
  children,
}: {
  label: string;
  hint: string;
  children: ReactNode;
}) {
  return (
    <div className="bg-surface-card px-4 py-2.5">
      <span className="text-overline uppercase tracking-wide text-txt-tertiary">{label}</span>
      <div className="mt-1 font-mono text-body font-semibold tabular-nums text-txt-primary">
        {children}
      </div>
      <div className="mt-1 text-[10px] leading-snug text-txt-tertiary">{hint}</div>
    </div>
  );
}

function LedgerRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border py-2 last:border-b-0">
      <span className="min-w-0 text-body-sm text-txt-secondary">
        {label}
        {hint ? <span className="text-txt-tertiary"> · {hint}</span> : null}
      </span>
      <span className="font-mono text-body-sm tabular-nums text-txt-secondary">{children}</span>
    </div>
  );
}
